import { Box, Button, Container, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import API from "../../api/API";
import LoadingOverlay from "../../components/LoadingOverlay";
import ButtonContinueIcon from "../../components/icons/ButtonContinueIcon";
import logo from "../../assets/cosmo-logo-gray.svg";
import { BOUGHT_FROM_OPTIONS, HEARD_FROM_OPTIONS } from "../../util/constants";
import ErrorPage from "../ErrorPage/ErrorPage";
import styles from "./QuestionPage.module.css";

const QuestionPage = () => {
  const [searchParams] = useSearchParams();
  const imei = searchParams.get("imei");
  const token = searchParams.get("token");

  const [channel, setChannel] = useState("");
  const [heardFrom, setHeardFrom] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const disableSubmit = useMemo(
    () => !imei || !token || !channel || !heardFrom || loading,
    [channel, heardFrom, imei, loading, token]
  );

  const handleChannelChange = (event: SelectChangeEvent<string>) => {
    setChannel(event.target.value);
  };

  const handleHeardFromChange = (event: SelectChangeEvent<string>) => {
    setHeardFrom(event.target.value);
  };

  const renderQuestionValue = (
    label: string,
    selected: string,
    options: { id: number; label: string }[],
  ) => {
    const selectedLabel = options.find((option) => String(option.id) === selected)?.label;

    return (
      <Box>
        <Typography sx={{ color: "#8f887e", fontSize: { xs: "14px", sm: "24px" }, lineHeight: 1.1, mb: { xs: "5px", sm: "8px" } }}>
          {label}
        </Typography>
        <Typography sx={{ color: selectedLabel ? "#1f1714" : "#afa68b", fontSize: { xs: "20px", sm: "34px" }, lineHeight: 1.1 }}>
          {selectedLabel ?? "Select an option"}
        </Typography>
      </Box>
    );
  };

  const submitHandler = async () => {
    if (!imei || !token) {
      setErrorMessage("Missing URL parameters");
      return;
    }

    setLoading(true);
    setErrorMessage(undefined);

    try {
      const response = await API.post("/devices/activation/question/submit", {
        imei,
        token,
        channel,
        heard_from: heardFrom,
      });
      window.location.href = response.data.successUrl ?? "/question/success";
    } catch (error: any) {
      setErrorMessage(error?.response?.data?.message ?? "Unknown Error");
    } finally {
      setLoading(false);
    }
  };

  if (!imei || !token) {
    return <ErrorPage message="Missing URL parameters" buttonConfig={{ showButton: false }} />;
  }

  if (errorMessage) {
    return <ErrorPage message={errorMessage} buttonConfig={{ showButton: false }} />;
  }

  return (
    <Container className={styles.page} style={{ minWidth: "100%", minHeight: "100%" }} disableGutters>
      <Box className={styles.form_column}>
        <div className={styles.logo_row}>
          <img src={logo} className={styles.logo} alt="cosmo logo" />
        </div>
        <h1 className={styles.title}>Finalize pairing</h1>
        <p className={styles.description}>
          We&apos;d like to know a little bit more about your purchase before we finish.
        </p>
        <div className={styles.imei_block}>
          <p className={styles.imei_label}>IMEI</p>
          <p className={styles.imei_value}>{imei}</p>
        </div>
        <div className={styles.form}>
          <FormControl fullWidth>
            <Select
              value={channel}
              onChange={handleChannelChange}
              displayEmpty
              sx={{
                borderRadius: "999px",
                backgroundColor: "#fdfaef",
                minHeight: { xs: "66px", sm: "112px" },
                "&.MuiOutlinedInput-root": {
                  backgroundColor: "#fdfaef",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fdfaef",
                  minHeight: { xs: "66px !important", sm: "112px !important" },
                  boxSizing: "border-box",
                  py: 0,
                  pl: { xs: "28px", sm: "58px" },
                  pr: { xs: "60px !important", sm: "86px !important" },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4cec3",
                  borderWidth: "2px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4cec3",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4cec3",
                  borderWidth: "2px",
                },
                "& .MuiSelect-icon": {
                  right: { xs: "28px", sm: "58px" },
                  color: "#8f887e",
                },
              }}
              renderValue={(selected) => renderQuestionValue(
                "Where did you buy your watch?",
                selected,
                BOUGHT_FROM_OPTIONS,
              )}
            >
              {BOUGHT_FROM_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={String(option.id)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Select
              value={heardFrom}
              onChange={handleHeardFromChange}
              displayEmpty
              sx={{
                borderRadius: "999px",
                backgroundColor: "#fdfaef",
                minHeight: { xs: "66px", sm: "112px" },
                "&.MuiOutlinedInput-root": {
                  backgroundColor: "#fdfaef",
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: "#fdfaef",
                  minHeight: { xs: "66px !important", sm: "112px !important" },
                  boxSizing: "border-box",
                  py: 0,
                  pl: { xs: "28px", sm: "58px" },
                  pr: { xs: "60px !important", sm: "86px !important" },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4cec3",
                  borderWidth: "2px",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4cec3",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4cec3",
                  borderWidth: "2px",
                },
                "& .MuiSelect-icon": {
                  right: { xs: "28px", sm: "58px" },
                  color: "#8f887e",
                },
              }}
              renderValue={(selected) => renderQuestionValue(
                "How did you first hear about Cosmo?",
                selected,
                HEARD_FROM_OPTIONS,
              )}
            >
              {HEARD_FROM_OPTIONS.map((option) => (
                <MenuItem key={option.id} value={String(option.id)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div className={styles.actions}>
            <Button
              variant="contained"
              onClick={submitHandler}
              disabled={disableSubmit}
              fullWidth
              endIcon={<ButtonContinueIcon />}
              sx={{
                minHeight: { xs: "46px", sm: "94px" },
                borderRadius: "999px",
                fontSize: { xs: "16px", sm: "32px" },
                fontWeight: 600,
                backgroundColor: "#1a73ee",
                "&:hover": {
                  backgroundColor: "#0d63df",
                },
                "& .MuiButton-endIcon": {
                  ml: { xs: "12px", sm: "28px" },
                  "& svg": {
                    width: { xs: "18px", sm: "36px" },
                    height: "auto",
                  },
                },
              }}
            >
              Finish pairing
            </Button>
          </div>
        </div>
        <LoadingOverlay loading={loading} />
      </Box>
    </Container>
  );
};

export default QuestionPage;
